import { useUserStore } from '~/store/user'
import { LOGIN_REDIRECT_QUERY_KEY, isAuthOptionalPath } from '~/utils/auth'

/**
 * Global auth guard. Resolves the session BEFORE a protected page renders, so an
 * anonymous visitor never renders the (auth-required) dashboard shell — no flash
 * and none of the page's authenticated requests fire (no 401 noise). Replaces
 * the previous implicit protection (the default layout's `clientDetails()` +
 * the 401 interceptor redirect).
 *
 * Public pages are detected by their shell: the `auth` layout (login, register,
 * sso exchange, social callback) and chromeless `layout: false` pages
 * (changelog, public downloads) require no session. Everything else (the
 * `default` dashboard and `console` layouts) is protected.
 *
 * Destination: checkout sends new buyers to sign-up; everything else to login —
 * both carry a `redirect` back to the originally requested page.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const layout = to.meta.layout
  if (layout === 'auth' || layout === false) {
    return
  }

  const userStore = useUserStore()
  const localePath = useLocalePath()

  /**
   * A signed-out visitor who asks for the checkout goes to the PUBLIC
   * configurator, not to a sign-up form.
   *
   * `/register?redirect=/billing/checkout` was where this funnel lost people:
   * the first thing a prospective customer was shown was a nine-field billing
   * address, before a single price. `/configure` prices the same servers with
   * no account at all and asks for those details last, once there is something
   * concrete to invoice — and it carries the configuration into the real
   * checkout afterwards.
   *
   * Everything else still goes to `/login` with a `redirect` back.
   */
  const redirectToAuth = () =>
    navigateTo(
      isAuthOptionalPath(to.path)
        ? localePath({ path: '/configure', query: to.query })
        : localePath({
            path: '/login',
            query: { [LOGIN_REDIRECT_QUERY_KEY]: to.fullPath },
          }),
      { redirectCode: 302 },
    )

  // Native credentials are local and explicit. A fresh installation has no
  // cookie to discover remotely; even offline it should open the login page.
  const nativeSession = useBffSession()
  if (nativeSession.isCapacitor && !await nativeSession.getSessionId()) {
    userStore.setSessionStatus('unauthenticated')
    return redirectToAuth()
  }

  if (userStore.sessionStatus === 'authenticated' && userStore.user) {
    return
  }

  if (userStore.sessionStatus === 'unauthenticated') {
    return redirectToAuth()
  }

  // Resolve the session. Only a definitive auth rejection (401/403) may send
  // the visitor to sign-in/sign-up: a transient failure (network blip, 5xx,
  // WHMCS read-after-write lag right after registration) used to be treated as
  // "unauthenticated" and bounced freshly-registered users from checkout back
  // to /register even though their session cookie was perfectly valid.
  const isAuthRejection = (error: unknown) => {
    const status = (error as { response?: { status?: number } })?.response?.status
    return status === 401 || status === 403
  }

  const CLIENT_DETAILS_ATTEMPTS = 2
  const RETRY_DELAY_MS = 300

  for (let attempt = 1; attempt <= CLIENT_DETAILS_ATTEMPTS; attempt++) {
    try {
      await userStore.clientDetails()
      userStore.setSessionStatus('authenticated')
      return
    } catch (error) {
      if (isAuthRejection(error)) {
        userStore.setSessionStatus('unauthenticated')
        return redirectToAuth()
      }
      if (attempt < CLIENT_DETAILS_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
      }
    }
  }

  // Still failing for a non-auth reason: fail open. The page renders and its
  // own requests surface the outage; sessionStatus stays 'unknown' so the next
  // navigation re-checks. Redirecting here would log out a valid session.
})
